import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";

/**
 * The strip above the nav.
 *
 * It promotes the free scan rather than a discount or a launch, because that
 * is the only thing on the site a visitor can get value from without an
 * account — which makes it the one message worth the permanent real estate at
 * the very top of every page.
 *
 * The verb is "Scan" because that is what the buttons say everywhere else
 * ("Scan it free", "Scan my resume"). An action that changes name between the
 * banner and the button is a second thing to learn for no reason.
 *
 * Dismissible and it stays dismissed. A banner that returns on every visit
 * stops being read within a week and starts costing 40px instead.
 */
const KEY = "nextopp_announcement_scan_v1";
const PROMOTES = "/ats-resume-checker";

export default function AnnouncementBar() {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
  });
  // A banner urging you to scan, on the scan page, is a link to where you
  // already are. Being on every public page is the point of this strip; being
  // on the one page it advertises is just noise at the top of a form.
  if (dismissed || pathname === PROMOTES) return null;

  const dismiss = () => {
    try { localStorage.setItem(KEY, "1"); } catch { /* shown again next time */ }
    setDismissed(true);
  };

  return (
    <div
      className="relative bg-night-deep text-white"
      data-testid="announcement-bar"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-90"
        style={{
          background:
            "linear-gradient(90deg, rgba(0,47,167,0.92), rgba(88,140,255,0.5) 52%, rgba(255,143,94,0.28))",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-12 sm:px-6 h-10 flex items-center justify-center gap-3 text-[13px]">
        <Link
          to="/ats-resume-checker"
          className="inline-flex items-center gap-1.5 font-medium hover:underline underline-offset-4 decoration-white/50"
          data-testid="announcement-link"
        >
          {/* The full sentence collides with the dismiss control at 390px, so
              the phrase that survives is the one carrying the offer. */}
          <span className="hidden sm:inline">Scan your resume against any job — free, no account</span>
          <span className="sm:hidden">Scan your resume — free</span>
          <ArrowRight className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss announcement"
          data-testid="announcement-dismiss"
          className="absolute right-4 p-1 rounded-sm text-white/60 hover:text-white hover:bg-white/10"
        >
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
